import { products } from '../data/mockData';

export const SITE_URL = new URL(process.env.REACT_APP_SITE_URL).origin;
const OPERATOR_URL = new URL(process.env.REACT_APP_PROGRAM_URL).origin;
// Content was revised on this date; never replace with a build/deployment timestamp.
export const CONTENT_MODIFIED = process.env.REACT_APP_CONTENT_MODIFIED;
if (!/^\d{4}-\d{2}-\d{2}$/.test(CONTENT_MODIFIED || '')) throw new Error('REACT_APP_CONTENT_MODIFIED must be the actual YYYY-MM-DD content revision date.');
export const publicMetadata = {
  '/': {
    title: 'At-Home Blood Testing, Personalized Beyond the Lab | HomeBloodKit',
    description: `Explore ${products.length} at-home panels and personalized protocols informed by your labs, intake and goals, plus wearable data and DNA insights when available.`,
  },
  '/order': {
    title: 'Integrated Wellness Optimization Activation | HomeBloodKit',
    description: 'Complete one optimization activation for your goals, health history and available data. The team reviews your intake to help select testing and personalize your next steps.',
  },
  '/results': {
    title: 'Lab Marker Guide: Understand Test Terms | HomeBloodKit',
    description: 'Learn what lab markers measure with our educational Lab Marker Guide. No personalized result interpretation, diagnosis or treatment recommendations.',
  },
  '/yara': {
    title: 'Ask Yara: Blood Testing Questions | HomeBloodKit',
    description: `Ask Yara about ${products.length} panel options, home collection and next steps. Educational support only; your care team leads panel selection and protocol development.`,
  },
  '/connected-care': {
    title: 'Your Data, Protocol & Next Steps | HomeBloodKit',
    description: 'Connect with your care team about results and personalized wellness guidance, informed by your history, goals and optional wearable and DNA data.',
  },
  '/privacy': {
    title: 'Privacy Policy | HomeBloodKit',
    description: 'Read how Integrated Wellness handles information on HomeBloodKit and learn about privacy when using our website and connected services.',
  },
  '/terms': {
    title: 'Terms of Service | HomeBloodKit',
    description: 'Read the HomeBloodKit terms for at-home wellness testing, educational tools, customization intake and the Integrated Wellness care-team workflow.',
  },
};
products.forEach((panel) => {
  publicMetadata[`/tests/${panel.id}`] = {
    title: `${panel.name} | HomeBloodKit`,
    description: `Explore ${panel.name}, $${panel.price}. Review tests and collection details, then complete intake for team-led panel selection and a path beyond lab results.`,
    panel,
  };
});

const privateTitles = {
  '/intake': 'Optimization Activation | HomeBloodKit', '/login': 'Staff Sign In | HomeBloodKit',
  '/account': 'Staff Account | HomeBloodKit', '/research': 'Staff Research | HomeBloodKit',
  '/connect': 'Assistant Access | HomeBloodKit', '/rhea': 'Ask Yara | HomeBloodKit',
};

export function getMetadata(pathname) {
  const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  const meta = publicMetadata[path];
  return {
    title: meta?.title || privateTitles[path] || 'Page Not Found | HomeBloodKit',
    description: meta?.description || 'Integrated Wellness HomeBloodKit.',
    canonical: meta ? `${SITE_URL}${path === '/' ? '' : path}` : null,
    robots: meta ? 'index, follow, max-image-preview:large' : 'noindex, nofollow',
    image: `${SITE_URL}/images/iwoptimizeme/full-kit.webp`,
    panel: meta?.panel,
  };
}

export function structuredData(pathname) {
  const meta = getMetadata(pathname);
  if (!meta.canonical) return [];
  const graph = [
    { '@type': 'WebSite', '@id': `${SITE_URL}/#website`, name: 'HomeBloodKit', url: SITE_URL, publisher: { '@id': `${OPERATOR_URL}/#organization` } },
    { '@type': 'Organization', '@id': `${OPERATOR_URL}/#organization`, name: 'Integrated Wellness', url: OPERATOR_URL, description: 'Team-led blood panel selection and personalized wellness protocols informed by available labs, intake, goals and optional wearable and DNA data.', telephone: '+12015353582' },
    { '@type': 'WebPage', '@id': `${meta.canonical}#webpage`, url: meta.canonical, name: meta.title, description: meta.description, isPartOf: { '@id': `${SITE_URL}/#website` }, dateModified: CONTENT_MODIFIED },
  ];
  if (meta.panel) {
    graph.push({ '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'HomeBloodKit', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: meta.panel.name, item: meta.canonical },
    ] });
    // No fixed purchasable Offer: intake determines the final customized testing plan.
    graph.push({ '@type': 'Service', name: meta.panel.name, url: meta.canonical, description: meta.panel.bestFor, serviceType: 'Blood test panel customization', provider: { '@id': `${OPERATOR_URL}/#organization` } });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
export function renderHead(pathname) {
  const m = getMetadata(pathname);
  const tags = [
    `<title>${escapeHtml(m.title)}</title>`,
    `<meta name="description" content="${escapeHtml(m.description)}">`,
    `<meta name="robots" content="${m.robots}">`,
    '<meta property="og:type" content="website">', '<meta property="og:site_name" content="HomeBloodKit">',
    `<meta property="og:title" content="${escapeHtml(m.title)}">`,
    `<meta property="og:description" content="${escapeHtml(m.description)}">`,
    `<meta property="og:image" content="${m.image}">`,
    '<meta name="twitter:card" content="summary_large_image">',
    `<meta name="twitter:title" content="${escapeHtml(m.title)}">`,
    `<meta name="twitter:description" content="${escapeHtml(m.description)}">`,
    `<meta name="twitter:image" content="${m.image}">`,
  ];
  if (m.canonical) tags.push(`<link rel="canonical" href="${m.canonical}">`, `<meta property="og:url" content="${m.canonical}">`, `<meta name="twitter:url" content="${m.canonical}">`);
  if (pathname.replace(/\/+$/, '') === '/intake') tags.push('<meta http-equiv="refresh" content="0;url=/order">');
  tags.push(`<script id="hbk-schema" type="application/ld+json">${JSON.stringify(structuredData(pathname)).replace(/</g, '\\u003c')}</script>`);
  return tags.join('\n');
}